import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, fmtEUR } from "@/lib/api";
import { toast } from "sonner";

const ALL_COUNTRIES = ["Kosovë", "Shqipëri", "Maqedoni e Veriut", "Mal i Zi"];

export default function Checkout() {
  const { cart, cartTotal, clearCart, user } = useApp();
  const nav = useNavigate();
  const [f, setF] = useState({
    first_name: "", last_name: "", phone: "", email: "", company_name: "",
    city: "", address: "", country: "Kosovë", note: "",
  });
  const [shippingFees, setShippingFees] = useState({ "Kosovë": 5 });
  const enabledCountries = Object.keys(shippingFees).length ? ALL_COUNTRIES.filter(c => Object.prototype.hasOwnProperty.call(shippingFees, c)) : ALL_COUNTRIES;
  const [loading, setLoading] = useState(false);
  const delivery = cart.length > 0 ? Number(shippingFees[f.country] ?? shippingFees["Kosovë"] ?? 5) : 0;
  const total = cartTotal + delivery;
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/settings").then(r => {
      if (r.data.shipping_fees && Object.keys(r.data.shipping_fees).length > 0) {
        setShippingFees(r.data.shipping_fees);
        // If current country is disabled, reset to first enabled
        const enabled = ALL_COUNTRIES.filter(c => Object.prototype.hasOwnProperty.call(r.data.shipping_fees, c));
        if (enabled.length > 0) setF(prev => enabled.includes(prev.country) ? prev : { ...prev, country: enabled[0] });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      const [first, ...rest] = (user.name || "").split(" ");
      // Compose address from street+house_number if set, else fallback to legacy `address`
      const composed = [user.street, user.house_number].filter(Boolean).join(" ").trim();
      const fullAddress = composed || user.address || "";
      const cityLine = [user.postal_code, user.city].filter(Boolean).join(" ").trim() || user.city || "";
      setF(prev => ({
        ...prev,
        first_name: prev.first_name || first || "",
        last_name: prev.last_name || rest.join(" ") || "",
        phone: prev.phone || user.phone || "",
        email: prev.email || user.email || "",
        company_name: prev.company_name || user.company_name || "",
        city: prev.city || cityLine,
        address: prev.address || fullAddress,
        country: user.country || prev.country || "Kosovë",
      }));
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/orders", {
        ...f,
        items: cart.map(i => ({ product_id: i.product_id, name: i.name, configuration: i.configuration, quantity: i.quantity, unit_price: i.unit_price, vat_rate: i.vat_rate ?? 18, image: i.image })),
        subtotal: cartTotal, delivery, total,
      });
      const orderNumber = r.data.order_number;
      nav(`/porosi-e-suksesshme/${orderNumber}`, { replace: true });
      setTimeout(() => clearCart(), 100);
    } catch { toast.error("Gabim gjatë porosisë"); setLoading(false); }
  };

  useEffect(() => { if (cart.length === 0 && !loading) nav("/shporta", { replace: true }); }, [cart.length, loading, nav]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-3xl sm:text-4xl uppercase mb-6">Pagesa</h1>
      <form onSubmit={submit} className="grid md:grid-cols-[1fr_320px] gap-6">
        <div className="hg-card p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Emri" data-testid="co-fname" value={f.first_name} onChange={upd("first_name")} className="bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
            <input required placeholder="Mbiemri" data-testid="co-lname" value={f.last_name} onChange={upd("last_name")} className="bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
          </div>
          <input placeholder="Emri i firmës (opsional)" data-testid="co-company" value={f.company_name} onChange={upd("company_name")} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
          <input required placeholder="Telefoni" data-testid="co-phone" value={f.phone} onChange={upd("phone")} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
          <input type="email" placeholder="Email (opsionale)" data-testid="co-email" value={f.email} onChange={upd("email")} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
          <input required placeholder="Adresa (rruga + numri)" data-testid="co-address" value={f.address} onChange={upd("address")} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
          <input required placeholder="Qyteti (dhe kodi postar)" data-testid="co-city" value={f.city} onChange={upd("city")} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
          <select value={f.country} onChange={upd("country")} data-testid="co-country"
            className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2.5">
            {enabledCountries.map(c => (
              <option key={c} value={c}>{c} — {fmtEUR(shippingFees[c] ?? 0)}</option>
            ))}
          </select>
          <textarea placeholder="Shënim (opsionale)" rows={3} data-testid="co-note" value={f.note} onChange={upd("note")} className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2.5" />
          <div className="border border-[#FFD600]/40 bg-[#FFD600]/5 px-3 py-3 text-sm">
            <div className="font-medium">Pagesë gjatë dorëzimit</div>
            <div className="text-xs text-neutral-400">Dërgesë në {f.country}</div>
          </div>
        </div>
        <div className="hg-card p-4 h-fit md:sticky md:top-20 space-y-2 text-sm">
          <div className="font-display text-xl uppercase mb-2">Porosia ({cart.length})</div>
          {cart.map(i => (
            <div key={i.key} className="flex justify-between text-xs border-b border-[#1F2B44] py-1">
              <span className="truncate mr-2">{i.name} × {i.quantity}</span><span>{fmtEUR(i.unit_price * i.quantity)}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2"><span className="text-neutral-400">Nëntotali</span><span>{fmtEUR(cartTotal)}</span></div>
          <div className="flex justify-between"><span className="text-neutral-400">Dërgesa ({f.country})</span><span>{fmtEUR(delivery)}</span></div>
          <div className="flex justify-between font-display text-2xl pt-2 border-t border-[#1F2B44]"><span>Totali</span><span className="text-[#FFD600]">{fmtEUR(total)}</span></div>
          <button disabled={loading} data-testid="place-order-btn" className="hg-btn-primary w-full py-3 uppercase text-sm mt-3 disabled:opacity-40">
            {loading ? "Duke dërguar..." : "Përfundo porosinë"}
          </button>
        </div>
      </form>
    </div>
  );
}
